import { VFC, useMemo, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Plane } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { cnoise21 } from '../../modules/glsl/noise';
import { PALETTE_HEX } from '../../constants/palettes';

const COLOR_PALETTES = PALETTE_HEX.map(([a, b, c]) => [new THREE.Color(a), new THREE.Color(b), new THREE.Color(c)] as [THREE.Color, THREE.Color, THREE.Color]);

type Palette = [string, string, string];

type BackgroundProps = {
	position?: [number, number, number];
	paletteA?: Palette;
	paletteB?: Palette;
	blend?: number;
	direction?: number;
}

export const Background: VFC<BackgroundProps> = ({ position = [0, 0, 0], paletteA, paletteB, blend = 0, direction = 1 }) => {
	const shader: THREE.Shader = useMemo(() => ({
		uniforms: {
			u_time: { value: 0 },
			u_mouse: { value: new THREE.Vector2() },
			u_color1: { value: COLOR_PALETTES[0][0].clone() },
			u_color2: { value: COLOR_PALETTES[0][1].clone() },
			u_color3: { value: COLOR_PALETTES[0][2].clone() },
		},
		vertexShader,
		fragmentShader,
	}), []);

	// 用于平滑过渡的目标颜色
	const targetColor1 = useRef(COLOR_PALETTES[0][0].clone());
	const targetColor2 = useRef(COLOR_PALETTES[0][1].clone());
	const targetColor3 = useRef(COLOR_PALETTES[0][2].clone());

	useEffect(() => {
		const sourceA = paletteA ?? PALETTE_HEX[0];
		const sourceB = paletteB ?? sourceA;
		const colorsA = sourceA.map(hex => new THREE.Color(hex)) as [THREE.Color, THREE.Color, THREE.Color];
		const colorsB = sourceB.map(hex => new THREE.Color(hex)) as [THREE.Color, THREE.Color, THREE.Color];

		// 更新目标颜色，而不是直接修改 uniforms
		targetColor1.current.copy(colorsA[0].clone().lerp(colorsB[0], blend));
		targetColor2.current.copy(colorsA[1].clone().lerp(colorsB[1], blend));
		targetColor3.current.copy(colorsA[2].clone().lerp(colorsB[2], blend));
	}, [paletteA, paletteB, blend]);

	const target = useMemo(() => new THREE.Vector2(), []);

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			// 移动端不跟随鼠标/触摸变换
			if (window.innerWidth <= 768) return;

			const x = e.clientX / window.innerWidth;
			const y = 1 - (e.clientY / window.innerHeight);
			target.set(x, y);
		};

		window.addEventListener('mousemove', handleMouseMove);
		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
		};
	}, [target]);

	useFrame((state, delta) => {
		// 使用delta时间确保动画速度与帧率无关
		// delta是秒为单位，乘以0.3保持原速度（0.005 * 60fps ≈ 0.3）
		shader.uniforms.u_time.value += delta * 0.3;

		// 防止时间无限累积，每个周期重置（sin的周期是2π）
		// 对于sin(u_time * 0.15)，完整周期约为41.89秒
		// 重置到一个周期内的等价值，避免精度问题
		const period = (2 * Math.PI) / 0.15; // ≈ 41.89
		if (shader.uniforms.u_time.value > period) {
			shader.uniforms.u_time.value = shader.uniforms.u_time.value % period;
		}

		shader.uniforms.u_mouse.value.lerp(target, 0.2);

		// 在每一帧中平滑过渡颜色 (0.05 的系数可以调节过渡速度，越小越慢)
		shader.uniforms.u_color1.value.lerp(targetColor1.current, 0.05);
		shader.uniforms.u_color2.value.lerp(targetColor2.current, 0.05);
		shader.uniforms.u_color3.value.lerp(targetColor3.current, 0.05);
	});

	return (
		<Plane args={[3, 3]} position={position}>
			<shaderMaterial args={[shader]} />
		</Plane>
	)
}

const vertexShader = `
varying vec2 v_uv;

void main() {
  v_uv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
}
`;

const fragmentShader = `
uniform float u_time;
uniform vec2 u_mouse;
uniform vec3 u_color1;
uniform vec3 u_color2;
uniform vec3 u_color3;
varying vec2 v_uv;

${cnoise21}

float random(vec2 p) {
  vec2 k1 = vec2(23.14069263277926, 2.665144142690225);
  return fract(cos(dot(p, k1)) * 12345.6789);
}

const vec3 black = vec3(0.0);

vec3 palette(float n, float ml) {
  float n1 = smoothstep(0.0, 0.2, n);
  vec3 col = mix(black, u_color1, n1);

  float n2 = smoothstep(0.1 + ml, 0.3 + ml, n);
  col = mix(col, u_color2, n2);

  float n3 = smoothstep(0.2 + ml, 0.4 + ml, n);
  col = mix(col, u_color3, n3);

  float n4 = smoothstep(0.3 + ml, 0.5 + ml, n);
  col = mix(col, black, n4);

  return col;
}

void main() {
  float mouseDist = length(u_mouse);
  float xFactor = u_mouse.x;
  float centerFactor = 1.0 - abs(xFactor - 0.5) * 2.0;
  // 弧度角度再小一点：减小 centerFactor 权重 (0.15 -> 0.05)
  float scaleFactor = 1.3 / (1.0 + xFactor * 0.6 + centerFactor * 0.01);
  scaleFactor = max(scaleFactor, 0.8);

  vec2 shared = (u_mouse + 0.3 * (mouseDist + 0.5));
  // 上下浮动循环动画：使用 sin(u_time) 实现往复运动
  // 速度更慢：u_time * 0.5
  // 距离更长：幅度从 -0.05 增加到 -0.15
  float bounce = sin(u_time * 0.15) * -0.3;
  vec2 seed = (v_uv + vec2(0.1, -0.02 + bounce)) * scaleFactor * shared;
  float noiseValue = cnoise21(seed) + mouseDist * 0.55;

  float ml = pow(mouseDist, 2.0) * 0.08;
  vec3 color = palette(noiseValue, ml);

  vec2 uvrandom = v_uv;
  uvrandom.y *= random(vec2(uvrandom.y, 0.4));
  color.rgb += random(uvrandom + u_time) * 0.01;

  gl_FragColor = vec4(color, 1.0);
}
`;


